import {
    OutliersConfigState,
    OutliersPlType,
    plTypeText,
} from 'widgets/outliers/store/config/OutliersConfigState';
import {
    isOutliersDataItem,
    OutliersData,
    OutliersDataItem,
} from 'widgets/outliers/data/outliersData';
import {
    BubbleMouseOver,
    BubbleMouseOverState,
} from 'widgets/outliers/components/chart/BubbleMouseOver';
import React, { useContext } from 'react';
import { useMappedStreamFields } from 'app/store/orders/mapping';
import AppContext from 'app/AppContext';
import { FloatingLegend } from 'utils/components/FloatingLegend/FloatingLegend';
import { useSelectIsFeatureEnabled } from 'app/store/featureToggles/selectors';
import { WellKnownFeatureToggleKeys } from 'app/store/featureToggles/FeatureTogglesState';
import { applyTickSize } from 'utils/tickSize';

export interface OutliersChartLegendsProps {
    chartHeight: number;
    config: OutliersConfigState;
    data: OutliersData;
    mouseOver: BubbleMouseOver;
}

export default function OutliersChartLegends(props: OutliersChartLegendsProps) {
    const { chartHeight, config, data, mouseOver } = props;

    const { benchmarkType, plType: plTypeConfig } = config;

    const isLastPriceEnabled = useSelectIsFeatureEnabled(
        WellKnownFeatureToggleKeys.LastPriceEnabled
    );
    const plType: OutliersPlType = isLastPriceEnabled
        ? plTypeConfig
        : 'realised';

    const { symbolField } = useMappedStreamFields();
    const { currencyFormat, numberFormat } = useContext(AppContext);

    const deriveLegendData = (mouseOverState: BubbleMouseOverState) => {
        if (mouseOverState?.orderIds) {
            if (mouseOverState.orderIds.length === 1) {
                return data.items.find(
                    (d) => d.orderID.toString() === mouseOverState.orderIds[0]
                );
            } else if (mouseOverState.orderIds.length > 1) {
                return mouseOverState.orderIds.length;
            } else {
                return undefined;
            }
        }
    };

    const renderLegends = (
        legendData: OutliersDataItem | number | undefined
    ) =>
        isOutliersDataItem(legendData) ? (
            <>
                {FloatingLegend.renderRow('Symbol', legendData[symbolField])}
                {FloatingLegend.renderRow(
                    `${plTypeText(plType)} P&L`,
                    `${currencyFormat.prefix}${numberFormat.n0(
                        legendData.pnl!
                    )}`
                )}
                {FloatingLegend.renderRow(
                    `P&L bps`,
                    `${numberFormat.n0(legendData.benchmarkPerformance!)}`
                )}
                {FloatingLegend.renderRow(
                    `${
                        benchmarkType === 'arrivalPrice'
                            ? 'Arrival Price'
                            : 'Adjusted Arrival Price'
                    }`,
                    `${currencyFormat.prefix}${applyTickSize(
                        numberFormat,
                        legendData.tickSize,
                        legendData.benchmarkPrice
                    )}`
                )}
            </>
        ) : (
            <div className="row ">
                <div className="value">{legendData} Orders, see grid</div>
            </div>
        );

    const deriveLegendClasses = (i: OutliersDataItem | number | undefined) =>
        isOutliersDataItem(i) ? FloatingLegend.toSideClass(i.side) : '';

    return (
        <FloatingLegend
            className="OutliersChartLegends"
            mouseOver={mouseOver}
            deriveLegendData={deriveLegendData}
            chartHeight={chartHeight}
            renderLegends={renderLegends}
            deriveLegendClasses={deriveLegendClasses}
        />
    );
}
